import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { Truck, Package, PlusSquare, Gavel, Handshake, Wallet, ShieldCheck, UserCircle, LogOut, Coins, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationsBell from "@/components/NotificationsBell";
import Logo from "@/components/Logo";
import { t } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const NAV = {
  shipper: [
    { to: "/my-loads", label: "My Loads", icon: Package },
    { to: "/post-load", label: "Post Load", icon: PlusSquare },
    { to: "/connections", label: "Connections", icon: Handshake },
    { to: "/wallet", label: "Wallet", icon: Wallet },
  ],
  transporter: [
    { to: "/marketplace", label: "Load Board", icon: Truck },
    { to: "/my-bids", label: "My Bids", icon: Gavel },
    { to: "/connections", label: "Connections", icon: Handshake },
    { to: "/wallet", label: "Wallet", icon: Wallet },
  ],
  admin: [
    { to: "/admin", label: "Approvals", icon: ShieldCheck },
    { to: "/marketplace", label: "Load Board", icon: Truck },
  ],
};

function StatusBanner({ user }) {
  if (user.role === "admin" || user.status === "approved") return null;
  const declined = user.status === "declined";
  return (
    <div data-testid="verification-banner" className={`flex items-start gap-3 px-4 py-3 text-sm border-b ${declined ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>
        {declined
          ? <>{t("Registration declined")}{user.admin_note ? `: ${user.admin_note}` : ""}. <Link to="/profile" className="underline font-semibold">{t("Update documents & resubmit")}</Link></>
          : <>{t("Verification in review — an admin is checking your registration papers. You can buy credits now; posting and bidding unlock once approved.")}</>}
      </span>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV[user.role] || [];

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 h-11 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-amber-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`;

  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-slate-900 text-white sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-2 px-5 h-16 border-b border-slate-800" data-testid="sidebar-logo">
          <Logo light className="text-2xl" />
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((i) => (
            <NavLink key={i.to} to={i.to} className={linkCls} data-testid={`nav-${i.label.toLowerCase().replace(/\s/g, "-")}`}>
              <i.icon className="w-4 h-4" /> {t(i.label)}
            </NavLink>
          ))}
          <NavLink to="/profile" className={linkCls} data-testid="nav-profile"><UserCircle className="w-4 h-4" /> {t("Profile")}</NavLink>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <p className="text-sm font-semibold truncate">{user.company_name || user.name}</p>
          <p className="font-mono text-xs text-slate-400 uppercase">{t(user.role)} · {user.public_code}</p>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn" className="mt-3 flex items-center gap-2 text-xs text-slate-400 hover:text-white">
            <LogOut className="w-3.5 h-3.5" /> {t("Sign out")}
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 sticky top-0 z-30">
          <Link to="/" className="md:hidden font-display font-black text-lg flex items-center gap-2">
            <Logo className="text-xl" />
          </Link>
          <p className="hidden md:block font-mono text-xs uppercase tracking-wider text-slate-500">{t("Logistics Marketplace")}</p>
          <div className="flex items-center gap-2">
            {user.role !== "admin" && (
              <Link to="/wallet" data-testid="nav-credit-balance" className="flex items-center gap-2 h-10 px-3 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                <Coins className="w-4 h-4 text-amber-400" />
                <span className="font-mono text-sm font-bold">{user.credits}</span>
                <span className="hidden sm:inline text-xs text-slate-400">{t("credits")}</span>
              </Link>
            )}
            <LanguageSwitcher compact />
            <NotificationsBell />
            <button onClick={() => { logout(); navigate("/login"); }} className="md:hidden p-2 text-slate-500" data-testid="logout-btn-mobile"><LogOut className="w-5 h-5" /></button>
          </div>
        </header>
        <StatusBanner user={user} />
        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>
      </div>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-slate-900 grid" style={{ gridTemplateColumns: `repeat(${items.length + 1}, 1fr)` }}>
        {[...items, { to: "/profile", label: "Profile", icon: UserCircle }].map((i) => (
          <NavLink key={i.to} to={i.to} data-testid={`mobile-nav-${i.label.toLowerCase().replace(/\s/g, "-")}`} className={({ isActive }) => `flex flex-col items-center justify-center h-16 text-[10px] gap-1 ${isActive ? "text-amber-400" : "text-slate-400"}`}>
            <i.icon className="w-5 h-5" /> {t(i.label)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
